'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { getSocket, disconnectSocket } from '@/lib/socket'
import type { Socket } from 'socket.io-client'

const SocketEvent = {
  RECORDING_CONSENT_REQUESTED: 'recording_consent_requested',
  RECORDING_CONSENT_RESPONSED: 'recording_consent_responsed',
} as const

type UseRecordingConsentProps = {
  consultationId: string
  patientId?: string
  autoRecording?: boolean
  recordingConsentPending?: boolean
  recordingConsent?: boolean
}

export function useRecordingConsent({ consultationId, patientId, autoRecording, recordingConsentPending, recordingConsent }: UseRecordingConsentProps) {
  const shouldAskConsent = !!recordingConsentPending || (!!autoRecording && recordingConsent == null)

  const [consentRequested, setConsentRequested] = useState(false)
  const socketRef = useRef<Socket | null>(null)

  const handleConsentRequested = (data?: { patientId?: string }) => {
    if (data?.patientId && data.patientId !== patientId) return
    setConsentRequested(true)
  }

  // Show consent modal if autoRecording or recordingConsentPending is true and patient hasn't consented yet
  useEffect(() => {
    if (shouldAskConsent) {
      setConsentRequested(true)
    } else {
      setConsentRequested(false)
    }
  }, [shouldAskConsent])

  // Connect socket and listen for recording consent requests from doctor during TLC
  useEffect(() => {
    const socket = getSocket()
    socketRef.current = socket

    if (!socket.connected) {
      socket.connect()
    }

    socket.emit('connectConsultation', consultationId)

    socket.on(SocketEvent.RECORDING_CONSENT_REQUESTED, handleConsentRequested)

    return () => {
      socket.off(SocketEvent.RECORDING_CONSENT_REQUESTED, handleConsentRequested)
      socket.emit('disconnectConsultation', consultationId)
      disconnectSocket()
    }
  }, [consultationId])

  const respondToConsent = useCallback(
    (accepted: boolean) => {
      setConsentRequested(false)
      socketRef.current?.emit(SocketEvent.RECORDING_CONSENT_RESPONSED, { consultationId, accepted })
    },
    [consultationId]
  )

  return {
    consentRequested,
    respondToConsent,
  }
}
